import { multiFactor, type TotpSecret, TotpMultiFactorGenerator } from 'firebase/auth';
import { toString as qrCodeToString } from 'qrcode';
import { initFirebase } from '../firebase.js';
import { AnmeldeFehler } from './sign-in-service.js';

export interface Einrichtung {
  geheimnis: string;
  qrCodeSvg: string;
}

export type BestaetigungsErgebnis = 'anspruch-da' | 'neu-anmelden';

export interface EinrichtungsDienst {
  starte(): Promise<Einrichtung>;
  bestaetige(code: string): Promise<BestaetigungsErgebnis>;
}

const ANZEIGENAME = 'Authenticator';
const AUSSTELLER = 'ERGO AWF';

export function firebaseEinrichtungsDienst(): EinrichtungsDienst {
  let geheimnis: TotpSecret | null = null;

  return {
    async starte() {
      const { auth } = await initFirebase();
      const nutzer = auth.currentUser;
      if (!nutzer) {
        throw new AnmeldeFehler('Bitte melden Sie sich an.');
      }
      try {
        const sitzung = await multiFactor(nutzer).getSession();
        geheimnis = await TotpMultiFactorGenerator.generateSecret(sitzung);
        const url = geheimnis.generateQrCodeUrl(nutzer.email ?? nutzer.uid, AUSSTELLER);
        // Lokal erzeugt, nicht von einem QR-Dienst geladen: externe Ressourcen sind
        // gesperrt, und das Geheimnis an einen fremden Host zu schicken waere falsch.
        const qrCodeSvg = await qrCodeToString(url, { type: 'svg', margin: 1 });
        return { geheimnis: geheimnis.secretKey, qrCodeSvg };
      } catch {
        throw new AnmeldeFehler(
          'Die Einrichtung ist gescheitert. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
        );
      }
    },

    async bestaetige(code) {
      const { auth } = await initFirebase();
      const nutzer = auth.currentUser;
      if (!nutzer || !geheimnis) {
        throw new AnmeldeFehler('Die Einrichtung ist abgelaufen. Bitte laden Sie die Seite neu.');
      }
      try {
        await multiFactor(nutzer).enroll(
          TotpMultiFactorGenerator.assertionForEnrollment(geheimnis, code),
          ANZEIGENAME,
        );
      } catch {
        throw new AnmeldeFehler(
          'Der Code ist nicht richtig. Bitte prüfen Sie die Zeit auf Ihrem Gerät.',
        );
      }
      geheimnis = null;

      // Ob die LAUFENDE Sitzung den Anspruch `sign_in_second_factor` nach dem Einschreiben
      // bekommt, ist nicht dokumentiert. Deshalb wird er gemessen, nicht angenommen: Token
      // erneuern und nachsehen. Fehlt er, muss der Nutzer sich neu anmelden -- sonst
      // schickt der Guard ihn sofort wieder hierher.
      const token = await nutzer.getIdTokenResult(true);
      const zweiter = (token.claims.firebase as { sign_in_second_factor?: unknown } | undefined)
        ?.sign_in_second_factor;
      return typeof zweiter === 'string' && zweiter.length > 0 ? 'anspruch-da' : 'neu-anmelden';
    },
  };
}
